"use client";

// S01 · Welcome (Claude Design · Attendee §01). First-run overlay, not a
// route: shown once per browser (localStorage), then never again. Browsing
// needs no account (R11), so the one amber action is "Show me what’s on"; the
// quiet line under it is for people who already signed up somewhere else —
// it opens S07 "Welcome back." (/auth?back=1).

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { EclipseMark } from "./Logo";
import { cn, FOCUS, PRESS } from "@/components/kit/cn";

const KEY = "mf:splash:v1";

/** Screens a person only reaches mid-sign-in — never interrupt them there. */
const SKIP_PREFIXES = ["/onboarding", "/auth/verify", "/auth/error"];

function markSeen() {
  try {
    window.localStorage.setItem(KEY, "1");
  } catch {
    // Storage blocked — the overlay simply shows again next visit.
  }
}

export function SplashGate() {
  const pathname = usePathname() ?? "/";
  const router = useRouter();
  const [show, setShow] = useState(false);
  const primary = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    if (SKIP_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`))) {
      markSeen();
      return;
    }
    let seen = true;
    try {
      seen = window.localStorage.getItem(KEY) === "1";
    } catch {
      // Private mode / storage blocked — treat as seen so we never trap anyone.
    }
    if (!seen) setShow(true);
    // Only the first path matters; later navigations are the overlay's own.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!show) return;
    primary.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") dismiss();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [show]);

  function dismiss() {
    markSeen();
    setShow(false);
  }

  if (!show) return null;

  const onAuth = pathname === "/auth" || pathname.startsWith("/auth/");

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="mf-welcome-title"
      className="mf-dark fixed inset-0 z-[100] overflow-hidden bg-pitch text-ink"
    >
      <div aria-hidden className="mf-glow pointer-events-none absolute inset-0" />

      {/* Phones: the S01 frame — copy centred, buttons on the thumb line.
          md+: one ≤480px column centred in the window, buttons right under
          the copy (never a phone frame floating in an empty canvas). */}
      <div className="relative mx-auto flex h-full w-full max-w-[480px] flex-col overflow-y-auto px-6 md:justify-center">
        <div className="flex flex-1 flex-col justify-center md:flex-none">
          <EclipseMark size={72} ground="pitch" />
          <h1 id="mf-welcome-title" className="mf-display mt-8 text-[40px] text-pretty">
            There’s a room with your name on it.
          </h1>
          <p className="mf-body mt-4 max-w-[300px] text-muted text-pretty">
            Concerts, workshops and slow evenings, made by people who care who shows up. Look around for free. Sign
            in when one feels like yours.
          </p>
        </div>

        <div className="flex flex-col gap-2 pb-[calc(2rem+env(safe-area-inset-bottom))] md:mt-12 md:pb-0">
          <Link
            ref={primary}
            href="/explore"
            onClick={dismiss}
            className={cn(
              "flex min-h-14 items-center justify-center rounded-full bg-gold px-6 py-4 text-[15px] leading-none font-semibold text-on-action",
              FOCUS,
              PRESS,
            )}
          >
            Show me what’s on
          </Link>
          {onAuth ? (
            <button
              type="button"
              onClick={() => {
                dismiss();
                // Keep ?next=… and switch /auth to its "Welcome back." face.
                const url = new URL(window.location.href);
                url.searchParams.set("back", "1");
                router.replace(`${url.pathname}${url.search}`);
              }}
              className={cn(
                "flex min-h-11 cursor-pointer items-center justify-center rounded-full text-[13px] font-medium text-muted transition-colors duration-[120ms] hover:text-ink",
                FOCUS,
              )}
            >
              I’ve been here before
            </button>
          ) : (
            <Link
              href="/auth?back=1"
              onClick={dismiss}
              className={cn(
                "flex min-h-11 items-center justify-center rounded-full text-[13px] font-medium text-muted transition-colors duration-[120ms] hover:text-ink",
                FOCUS,
              )}
            >
              I’ve been here before
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
